import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { FaBell, FaExclamationCircle, FaInfoCircle, FaExclamationTriangle } from 'react-icons/fa';
import { fetchData } from '../../../helpers/fetchData';
import ResueableSibebar from '../../../shared/ReuseableSidebar';
import CustomButton from '../../../shared/CustomButton';
import { useLocation } from 'react-router-dom';
import i18next from 'i18next';

const PatientAlertsDisplay = ({ patientId, role }) => {
  const { t } = useTranslation();
  const [alerts, setAlerts] = useState([]);
  const [showDrawer, setShowDrawer] = useState(false);
 const location = useLocation();
  const auth = useSelector(state => state.auth);
  const user = auth?.user;
  const isProfilingLayout = location.pathname.includes('/patient-profile')

  const userRoleName = user?.role?.name_en?.toLowerCase() || '';
  const userRoleId =
    user?.role_id ||
    user?.role?.id ||
    user?.role?._id ||
    user?.role?.role_id ||
    auth?.roleData?.role_id ||
    '';
  const currentRole = role?.toLowerCase() || userRoleName;

    const fetchAlerts = async () => {
      if (!patientId) {
        setAlerts([]);
        return;
      }

      try {
        const res = await fetchData('POST', '/v3/patient-alert/getByPatient', {
          patient_id: patientId,
          role_id: String(userRoleId)
        });

        if (res?.data && Array.isArray(res.data)) {
          const filteredAlerts = res.data.filter(alert => {
            if (!alert.is_active) return false;
            if (!alert.roles || alert.roles.length === 0) return true;
            return alert.roles.some(r => {
              const alertRoleId = typeof r === 'string' ? r : (r.role_id || r.id || r._id);
              const alertRoleName = typeof r === 'string' ? r.toLowerCase() : r.role_name?.toLowerCase();

              const matchId = alertRoleId && userRoleId && String(alertRoleId) === String(userRoleId);
              const matchName = alertRoleName && currentRole && alertRoleName === currentRole;

              return matchId || matchName;
            });
          });

          setAlerts(filteredAlerts);
          if (filteredAlerts.length > 0 && !isProfilingLayout) {
            setShowDrawer(true);
          }
        } else {
          setAlerts([]);
        }
      } catch (error) {
        console.error("Failed to fetch alerts:", error);
        setAlerts([]);
      }
    };

  const historyRefreshTrigger = useSelector(state => state.beneficiary?.historyRefreshTrigger);
  
  useEffect(() => {
    fetchAlerts();
  }, [patientId, currentRole, userRoleId, location.pathname, historyRefreshTrigger]);

  const groupedAlerts = useMemo(() => {
    return alerts.reduce((acc, alert) => {
      const type = alert.alert_type || 'info';
      if (!acc[type]) acc[type] = [];
      acc[type].push(alert);
      return acc;
    }, {});
  }, [alerts]);

  const alertOrder = ['danger', 'warning', 'info', 'success'];

  if (alerts.length === 0) return null;

  const getAlertIcon = (type) => {
    switch (type) {
      case 'danger': return <FaExclamationCircle className="text-red-500" size={18} />;
      case 'warning': return <FaExclamationTriangle className="text-amber-500" size={18} />;
      case 'success': return <FaExclamationCircle className="text-green-500" size={18} />;
      default: return <FaInfoCircle className="text-blue-500" size={18} />;
    }
  };

  const getCategoryStyles = (type) => {
    switch (type) {
      case 'danger': return { text: 'text-red-600', border: 'border-red-100', accent: 'bg-red-500' };
      case 'warning': return { text: 'text-amber-600', border: 'border-amber-100', accent: 'bg-amber-500' };
      case 'success': return { text: 'text-green-600', border: 'border-green-100', accent: 'bg-green-500' };
      default: return { text: 'text-blue-600', border: 'border-blue-100', accent: 'bg-blue-500' };
    }
  };

  return (
    <>
      {!showDrawer && isProfilingLayout && (
        <div
          onClick={() => setShowDrawer(true)}
          className={`fixed top-3 flex flex-col items-end cursor-pointer group pointer-events-auto ${i18next.language=="ar"?"left-24":"right-24"}`}
        >
          <div className="bg-red-600 text-white px-2 py-2 rounded-lg flex items-center gap-1.5">
            <FaBell size={13} />
            <span className="text-[12px] font-semibold">{alerts.length}</span>
          </div>
        </div>
      )}

      <ResueableSibebar
        isVisible={showDrawer}
        setIsVisible={setShowDrawer}
        title={t('PatientAlerts')}
        HeaderIcon={FaBell}
        position="right"
      >
        <div className="flex flex-col h-full overflow-hidden">
          <div className="flex-1 overflow-y-auto pr-1 scrollbar-hide py-2">
            <div className="flex flex-col gap-6 mt-2 pb-4">
              {alertOrder.map(type => {
                const typeAlerts = groupedAlerts[type];
                if (!typeAlerts || typeAlerts.length === 0) return null;
                const styles = getCategoryStyles(type);

                return (
                  <div key={type} className="flex flex-col gap-2">
                    <div className={`flex items-center gap-2 ${styles.border}`}>
                      {getAlertIcon(type)}
                      <h3 className={`text-[12px] font-semibold uppercase tracking-widest ${styles.text}`}>
                        {t(type)} ({typeAlerts.length})
                      </h3>
                    </div>
                    <div className="flex flex-col gap-2 ml-1">
                      {typeAlerts?.map((alert, idx) => (
                        <div key={alert.id || alert._id || idx} className="bg-white p-2 rounded-lg border border-gray-200 relative overflow-hidden">
                          <p className="text-[13px] font-normal text-gray-700 leading-snug break-words">
                            {alert.message}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* <div className="pt-4 mt-auto border-t border-gray-200 bg-gray-50/50 -mx-4 px-4 pb-4">
            <CustomButton 
              label={t('Understood')}
              onClick={() => setShowDrawer(false)}
              className="w-full font-bold"
              buttonType="primary"
            />
          </div> */}

        </div>
      </ResueableSibebar>
    </>
  );
};

export default PatientAlertsDisplay;
